import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { AppProvider } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import { initDarkMode } from "@/lib/theme";
import { isBot, renderForBot } from "@/lib/bots";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { installGlobalCrashHandlers } from "@/lib/crashReport";
import { installProfileGateToastFilter } from "@/lib/toastGate";
import { installOfflineQueue } from "@/lib/offlineQueue";
import { installVolumeKeys } from "@/lib/volumeKeys";
import { installFlightRecorder } from "@/lib/flightRecorder";
import { installMediaFocus } from "@/lib/mediaFocus";
import { armSplashFallback } from "@/lib/splash"; // lot 702 — splash natif gardé jusqu'au 1er rendu, filet 6 s
import { installPlatformClass } from "@/lib/platformClass"; // lot 703 — html.plat-android (flous → fonds pleins, Android seul)
import { captureRefCode } from "@/lib/referral"; // lot 705 — code d'invitation ?ref= mémorisé dès l'entrée

initDarkMode();
installPlatformClass(); // avant le 1er rendu : l'en-tête est déjà « plein » à la première image
captureRefCode(); // web : https://yalola.app/?ref=XXXX (les liens natifs passent par lib/deepLinks.js)
installGlobalCrashHandlers();
armSplashFallback();
installFlightRecorder();
installProfileGateToastFilter();
installOfflineQueue();
installVolumeKeys();
installMediaFocus();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

const mount = () => {
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AppProvider>
            <App />
            <Toaster richColors position="top-right" />
          </AppProvider>
        </QueryClientProvider>
      </AppErrorBoundary>
    </React.StrictMode>
  );
};

// Robots (Googlebot, Bingbot, aperçus…) : HTML serveur de la route courante ; humains : le SPA.
if (isBot()) {
  renderForBot(process.env.REACT_APP_BACKEND_URL).then((done) => { if (!done) mount(); }).catch(mount);
} else {
  mount();
}
